import React, { useState, useMemo, useEffect } from 'react';
import { ITEMS_PER_PAGE_OPTIONS } from '../../utils/pagination';
import TableExportActions from './TableExportActions';
import './DataTable.css';

const DataTable = ({ 
  data = [], 
  columns = [], 
  loading = false, 
  loadingMessage = "Cargando datos...",
  emptyMessage = "No hay datos para mostrar",
  className = "",
  itemsPerPageOptions = ITEMS_PER_PAGE_OPTIONS,
  defaultItemsPerPage = 10,
  externalPagination = false,
  externalTotalItems = 0,
  externalTotalPages = 0,
  externalCurrentPage = 1,
  onPageChange = () => {},
  onItemsPerPageChange = () => {},
  exportFileName = 'grilla',
  exportTitle = 'Exportación de grilla',
  hideExport = false
}) => {
  const containerClassName = ['data-table-container', 'data-table-basic', className]
    .filter(Boolean)
    .join(' ');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  useEffect(() => {
    setItemsPerPage(defaultItemsPerPage);
    if (!externalPagination) {
      setCurrentPage(1);
    }
  }, [defaultItemsPerPage, externalPagination]);

  useEffect(() => {
    if (!externalPagination) {
      setCurrentPage(1);
    }
  }, [data, itemsPerPage, externalPagination]);

  // Función para ordenar los datos
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Manejar valores nulos/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

      // Comparar fechas
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === 'asc' 
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Comparar strings (case insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Comparar números
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Fallback: convertir a string y comparar
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      const comparison = aStr.localeCompare(bStr);
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  const totalItems = externalPagination ? externalTotalItems : sortedData.length;
  const totalPages = Math.max(
    1,
    externalPagination ? externalTotalPages : Math.ceil(totalItems / itemsPerPage)
  );
  const safeCurrentPage = Math.min(
    Math.max(externalPagination ? externalCurrentPage : currentPage, 1),
    totalPages
  );
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = externalPagination ? sortedData : sortedData.slice(startIndex, endIndex);
  const startItem = totalItems === 0 ? 0 : startIndex + 1;
  const endItem = totalItems === 0 ? 0 : Math.min(startIndex + paginatedData.length, totalItems);

  // Función para manejar el click en las columnas
  const handleSort = (columnKey) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column || column.sortable === false) return;

    setSortConfig(prevConfig => ({
      key: columnKey,
      direction: prevConfig.key === columnKey && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Función para obtener el ícono de ordenamiento
  const getSortIcon = (columnKey) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column || column.sortable === false) return null;

    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort sort-icon inactive"></i>;
    }

    return sortConfig.direction === 'asc' 
      ? <i className="fas fa-sort-up sort-icon active"></i>
      : <i className="fas fa-sort-down sort-icon active"></i>;
  };

  // Función para renderizar el contenido de una celda
  const renderCellContent = (item, column) => {
    const value = item[column.key];
    
    // Si hay una función de renderizado personalizada
    if (column.render && typeof column.render === 'function') {
      return column.render(value, item);
    }

    // Si hay un formateador
    if (column.formatter && typeof column.formatter === 'function') {
      return column.formatter(value);
    }

    // Renderizado por defecto
    if (value == null) return '-';
    
    // Si es una fecha, formatearla
    if (value instanceof Date) {
      return value.toLocaleDateString('es-AR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    return String(value);
  };

  const handleItemsPerPageChange = (event) => {
    const nextItemsPerPage = Number(event.target.value) || defaultItemsPerPage;
    setItemsPerPage(nextItemsPerPage);
    if (externalPagination) {
      onItemsPerPageChange(nextItemsPerPage);
      return;
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    if (externalPagination) {
      onPageChange(page);
      return;
    }
    setCurrentPage(page);
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, safeCurrentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (totalPages <= 1) {
      return [
        <button key="single" type="button" className="pagination-btn active" disabled>
          1
        </button>
      ];
    }

    if (startPage > 1) {
      buttons.push(
        <button key="first" type="button" className="pagination-btn" onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis-start" className="pagination-ellipsis">...</span>);
      }
    }

    for (let page = startPage; page <= endPage; page += 1) {
      buttons.push(
        <button
          key={page}
          type="button"
          className={`pagination-btn ${page === safeCurrentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis-end" className="pagination-ellipsis">...</span>);
      }
      buttons.push(
        <button key="last" type="button" className="pagination-btn" onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  if (loading) {
    return (
      <div className={containerClassName}>
        <div className="data-table-loading">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p>{loadingMessage}</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={containerClassName}>
        <div className="data-table-empty">
          <div className="empty-icon">
            <i className="fas fa-inbox"></i>
          </div>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.key}
                  className={`
                    ${column.sortable !== false ? 'sortable' : ''} 
                    ${column.align ? `text-${column.align}` : ''} 
                    ${column.width ? `width-${column.width}` : ''}
                    ${column.className || ''}
                  `}
                  onClick={() => handleSort(column.key)}
                  style={column.style}
                >
                  <div className="th-content">
                    <span className="th-text">{column.title}</span>
                    {getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={item.id || index} className="data-row">
                {columns.map((column) => (
                  <td 
                    key={`${item.id || index}-${column.key}`}
                    className={`
                      ${column.align ? `text-${column.align}` : ''} 
                      ${column.className || ''}
                    `}
                    style={column.style}
                  >
                    {renderCellContent(item, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination-container data-table-pagination">
        <div className="pagination-left-group">
          <div className="items-per-page-container">
            <label htmlFor="table-items-per-page">Mostrar</label>
            <select
              id="table-items-per-page"
              className="items-per-page-select"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span>registros</span>
          </div>
          {!hideExport && (
            <TableExportActions
              columns={columns}
              rows={sortedData}
              fileName={exportFileName}
              title={exportTitle}
              compact
            />
          )}
        </div>

        <div className="pagination-info">
          Mostrando {startItem}-{endItem} de {totalItems}
        </div>

        <div className="pagination-controls">
          <button
            type="button"
            className="pagination-btn nav-btn"
            onClick={() => handlePageChange(safeCurrentPage - 1)}
            disabled={safeCurrentPage === 1 || totalPages === 1}
            aria-label="Pagina anterior"
          >
              <i className="fas fa-chevron-left"></i>
          </button>
          {renderPaginationButtons()}
          <button
            type="button"
            className="pagination-btn nav-btn"
            onClick={() => handlePageChange(safeCurrentPage + 1)}
            disabled={safeCurrentPage === totalPages || totalPages === 1}
            aria-label="Pagina siguiente"
          >
              <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
